import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Switch,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Button,
  Typography,
  Divider,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

const defaultOptions = [
  { value: 'coffee', labelKey: 'coffee' },
  { value: 'small-coffee', labelKey: 'small_coffee' },
  { value: 'tea', labelKey: 'tea' },
  { value: 'ice-tea', labelKey: 'ice_tea' },
  { value: 'energy-drink', labelKey: 'energy_drink' },
  { value: 'jar', labelKey: 'jar' },
  { value: 'chocolate-bar', labelKey: 'chocolate_bar' }
];

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 24px 0 0 0;
  gap: 8px;

  & > .MuiTextField-root {
    margin-bottom: 16px;
  }
`

export default function CaffeineConfigurationForm({
  initialConfigurations = [],
  onSubmit,
  canEditExisting = false,
  products = [],
}) {
  const { t } = useTranslation('common');
  const [configurations, setConfigurations] = useState(initialConfigurations);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setConfigurations(initialConfigurations);
  }, [initialConfigurations]);

  const handleCaffeineChange = (index, field, value) => {
    const updated = [...configurations];
    updated[index] = { ...updated[index], [field]: value };
    setConfigurations(updated);
  };

  const handleAddRow = () => {
    setConfigurations([
      ...configurations,
      {id: '', caffeine_product_id: '', enabled: true, name: '', description: '', product_image_tag: '', caffeine_mg: '' },
    ]);
  };

  const normalizeName = name => (name || '').trim().toLowerCase();
  const availableProducts = products.filter(product =>
    normalizeName(product.name) !== 'no caffeine' &&
    !initialConfigurations.some(config => config.id && normalizeName(config.name) === normalizeName(product.name)));
  const productsForRow = index => availableProducts.filter(product =>
    !configurations.some((config, rowIndex) => rowIndex !== index &&
      normalizeName(config.name) === normalizeName(product.name)));

  const handleProductChange = (index, id) => {
    const product = productsForRow(index).find(product => String(product.id) === String(id));
    if (!product) return;
    setConfigurations(rows => rows.map((row, rowIndex) => rowIndex === index
      ? { ...row, caffeine_product_id: product.id, name: product.name || '',
          caffeine_mg: product.caffeine_mg ?? '', description: product.description || '',
          product_image_tag: product.product_image_tag || '' }
      : row));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onSubmit(configurations.filter(config => canEditExisting || !config.id));
    } finally {
      setSaving(false);
    }
  };

  const hasIncompleteRow = configurations.some(config => !config.id && !config.caffeine_product_id);
  const hasSavableRows = configurations.some(config => canEditExisting || !config.id);

   return (
    <FormContainer as="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        {t('account_config.caffeine_form.title')}
      </Typography>

      {configurations.map((config, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px',
            flexWrap: 'wrap',
          }}
        >
          {!config.id && (
            <TextField
              select
              required
              disabled={saving}
              label={t('account_config.caffeine_form.select_product')}
              value={config.caffeine_product_id || ''}
              onChange={event => handleProductChange(index, event.target.value)}
              style={{ minWidth: 240 }}
            >
              {productsForRow(index).map(product => (
                <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>
              ))}
            </TextField>
          )}
          <FormControlLabel
            control={
              <Switch
                disabled={saving || !config.id || !canEditExisting}
                checked={!!config.enabled}
                onChange={(e) => handleCaffeineChange(index, 'enabled', e.target.checked)}
              />
            }
            label={t('account_config.caffeine_form.active')}
          />

          <TextField
            disabled={saving || !config.id || !canEditExisting}
            label={t('account_config.caffeine_form.name')}
            name="name"
            value={config.name || ''}
            onChange={(e) => handleCaffeineChange(index, 'name', e.target.value)}
            style={{ width: '16%' }}
          />

          <TextField
            disabled={saving || !config.id || !canEditExisting}
            type="number"
            label={t('account_config.caffeine_form.caffeine_mg')}
            name="caffeine_mg"
            value={config.caffeine_mg ?? ''}
            onChange={(e) => handleCaffeineChange(index, 'caffeine_mg', e.target.value)}
            style={{ width: '8%' }}
          />

          <FormControl disabled={saving || !config.id || !canEditExisting} style={{ minWidth: 200 }}>
            <InputLabel>{t('account_config.caffeine_form.icon')}</InputLabel>
            <Select
              value={config.product_image_tag || ''}
              onChange={(e) => handleCaffeineChange(index, 'product_image_tag', e.target.value)}
            >
              {config.product_image_tag && !defaultOptions.some(opt => opt.value === config.product_image_tag) && (
                <MenuItem value={config.product_image_tag}>{config.product_image_tag}</MenuItem>
              )}
              {defaultOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(`account_config.caffeine_form.options.${opt.labelKey}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            disabled={saving || !config.id || !canEditExisting}
            label={t('account_config.caffeine_form.description')}
            name="description"
            value={config.description || ''}
            onChange={(e) => handleCaffeineChange(index, 'description', e.target.value)}
            style={{ width: '32%' }}
          />
          <Divider style={{ margin: '20px 0' }} />
        </div>
      ))}

      <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
        <Button variant="outlined" onClick={handleAddRow} disabled={saving || productsForRow(-1).length === 0}>
          {t('account_config.caffeine_form.add_product')}
        </Button>

        <Button disabled={saving || hasIncompleteRow || !hasSavableRows} type="submit" variant="contained" color="primary">
          {t('account_config.caffeine_form.save_settings')}
        </Button>
      </div>
    </FormContainer>
  )
}
